import { useBreakpoint, VSpacing } from '@hh.ru/magritte-ui';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import Field from 'src/components/ResumeCard/Body/Field';
import Divider from 'src/components/ResumeCard/Divider';
import HiddenByApplicant from 'src/components/ResumeCard/HiddenByApplicant';
import translation from 'src/components/translation';
import { useOnOffState } from 'src/hooks/useOnOffState';
import { useSelector } from 'src/hooks/useSelector';
import { HiddenFields } from 'src/models/resume/resumeCommon.types';
import type { ResumeCardData } from 'src/models/resumeCard';
import { UserType } from 'src/models/userType';

import ContactsClosed from 'src/components/ResumeCard/Body/Contacts/Closed';
import DeepLinks from 'src/components/ResumeCard/Body/Contacts/DeepLinks';
import ContactsOpen from 'src/components/ResumeCard/Body/Contacts/Open';

const TrlKeys = {
    title: 'resume.search.field.contacts',
    hidden: 'resume.hiddenFields.phones',
};

interface Props {
    resume: ResumeCardData;
    isNew: boolean;
    markAsRead?: () => void;
    showDeepLinks: boolean;
    visited: boolean;
    isResponse: boolean;
    goal?: string;
    vacancyId: string;
    onContactsClick?: () => void;
}

const Contacts: TranslatedComponent<Props> = ({
    resume,
    showDeepLinks,
    visited,
    markAsRead,
    goal,
    isResponse,
    vacancyId,
    onContactsClick,
    trls,
}) => {
    const { hiddenFields, preferredContact, phone: phones, topicId } = resume;
    const { id: resumeId, hash: resumeHash, sim_hash: simHash, created } = resume._attributes;

    const isBackoffice = useSelector((state) => state.userType === UserType.BackOffice);
    const [isOpenContacts, openContacts] = useOnOffState(resume._attributes.openedContacts === 'true' || isBackoffice);

    const { isGtM } = useBreakpoint();

    if (hiddenFields.some((field) => field.string === HiddenFields.Phones)) {
        return (
            <Field title={trls[TrlKeys.title]} dataQaName="hidden-contacts">
                <HiddenByApplicant>{trls[TrlKeys.hidden]}</HiddenByApplicant>
            </Field>
        );
    }

    if (!phones?.length) {
        return null;
    }

    const preferredPhone = preferredContact?.[0].string;
    const firstPhone = phones[0];

    return (
        <Field title={trls[TrlKeys.title]} dataQaName="contacts">
            {isOpenContacts ? (
                <ContactsOpen phones={phones} preferredPhone={preferredPhone} />
            ) : (
                <ContactsClosed
                    phone={firstPhone}
                    preferredPhone={preferredPhone}
                    openContacts={openContacts}
                    markAsRead={markAsRead}
                    resumeId={resumeId}
                    resumeHash={resumeHash}
                    goal={goal}
                    vacancyId={vacancyId}
                />
            )}
            {showDeepLinks && (
                <>
                    {isOpenContacts || !isGtM ? <VSpacing default={8} /> : <Divider />}
                    <DeepLinks
                        phone={firstPhone}
                        resumeId={resumeId}
                        resumeHash={resumeHash}
                        simHash={simHash}
                        created={created}
                        markAsRead={markAsRead}
                        visited={visited}
                        isResponse={isResponse}
                        topicId={topicId}
                        onContactsClick={onContactsClick}
                    />
                </>
            )}
        </Field>
    );
};

export default translation(Contacts);
